import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Building2, 
  Users, 
  FileText, 
  AlertTriangle, 
  Clock, 
  Plus,
  ArrowRight,
  TrendingUp
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];

const Dashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [token]);

  const getStatusBadge = (status) => {
    const styles = {
      active: 'status-active',
      inactive: 'status-inactive',
      pending_review: 'status-pending'
    };
    return styles[status] || 'status-inactive';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Overview of your supplier management</p>
        </div>
        <Link to="/suppliers/new">
          <Button data-testid="add-supplier-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Supplier
          </Button>
        </Link>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="kpi-total-suppliers">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Suppliers</p>
                <p className="text-3xl font-bold font-heading mt-1">{stats?.total_suppliers || 0}</p>
              </div>
              <div className="p-3 bg-primary/10 rounded-lg">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="kpi-onboarded-suppliers">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Onboarded</p>
                <p className="text-3xl font-bold font-heading mt-1 text-green-600 dark:text-green-400">
                  {stats?.onboarded_suppliers || 0}
                </p>
              </div>
              <div className="p-3 bg-green-500/10 rounded-lg">
                <Users className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="kpi-expiring-docs">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Expiring Documents</p>
                <p className="text-3xl font-bold font-heading mt-1 text-orange-600 dark:text-orange-400">
                  {stats?.expiring_count || 0}
                </p>
              </div>
              <div className="p-3 bg-orange-500/10 rounded-lg">
                <Clock className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="kpi-expired-docs">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Expired Documents</p>
                <p className="text-3xl font-bold font-heading mt-1 text-red-600 dark:text-red-400">
                  {stats?.expired_count || 0}
                </p>
              </div>
              <div className="p-3 bg-red-500/10 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Suppliers by Sector */}
        <Card data-testid="chart-by-sector">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Suppliers by Sector
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.suppliers_by_sector?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={stats.suppliers_by_sector}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis 
                    dataKey="sector" 
                    tick={{ fontSize: 12 }} 
                    className="text-muted-foreground"
                    tickLine={false}
                  />
                  <YAxis tick={{ fontSize: 12 }} className="text-muted-foreground" tickLine={false} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Suppliers by Area */}
        <Card data-testid="chart-by-area">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Suppliers by Area
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.suppliers_by_area?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={stats.suppliers_by_area}
                    dataKey="count"
                    nameKey="area"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ area, count }) => `${area}: ${count}`}
                  >
                    {stats.suppliers_by_area.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Suppliers */}
        <Card data-testid="recent-suppliers">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-heading">Recently Added</CardTitle>
            <Link to="/suppliers">
              <Button variant="ghost" size="sm">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.recent_suppliers?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_suppliers.map((supplier) => (
                  <Link 
                    key={supplier.supplier_id} 
                    to={`/suppliers/${supplier.supplier_id}`}
                    className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                    data-testid={`recent-supplier-${supplier.supplier_id}`}
                  >
                    <div>
                      <p className="font-medium">{supplier.company_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(supplier.sectors || [supplier.sector]).slice(0, 2).join(', ')}
                        {(supplier.sectors || []).length > 2 && ` +${supplier.sectors.length - 2}`}
                      </p>
                    </div>
                    <Badge className={`${getStatusBadge(supplier.status)} capitalize`}>
                      {supplier.status.replace('_', ' ')}
                    </Badge>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Building2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No suppliers yet</p>
                <Link to="/suppliers/new">
                  <Button variant="link" className="mt-2">Add your first supplier</Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Expiring Documents Alert */}
        <Card className="border-t-2 border-t-orange-500" data-testid="expiring-documents-alert">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Document Alerts
            </CardTitle>
            <Link to="/documents">
              <Button variant="ghost" size="sm">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {(stats?.expiring_documents?.length > 0 || stats?.expired_documents?.length > 0) ? (
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {stats.expired_documents?.slice(0, 3).map((doc) => (
                  <div 
                    key={doc.document_id} 
                    className="flex items-center justify-between p-3 rounded-lg bg-red-500/5 border border-red-500/20"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-red-500" />
                      <div>
                        <p className="font-medium text-sm">{doc.original_filename}</p>
                        <p className="text-xs text-muted-foreground">{doc.company_name}</p>
                      </div>
                    </div>
                    <Badge className="status-expired">Expired</Badge>
                  </div>
                ))}
                {stats.expiring_documents?.slice(0, 3).map((doc) => (
                  <div 
                    key={doc.document_id} 
                    className="flex items-center justify-between p-3 rounded-lg bg-orange-500/5 border border-orange-500/20"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-orange-500" />
                      <div>
                        <p className="font-medium text-sm">{doc.original_filename}</p>
                        <p className="text-xs text-muted-foreground">
                          {doc.company_name} • Expires {formatDate(doc.expiry_date)}
                        </p>
                      </div>
                    </div>
                    <Badge className="status-warning">Expiring</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No document alerts</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
